// Shore field: a world-space map of "how far is the nearest land" over the board and a margin of open sea.
// The water shader samples it (one texture, bilinear) instead of colouring each water tile by its own class,
// which is what made every hex of the sea visible: colour, foam and shallows now flow continuously across tile
// borders. Built once per terrain signature on the CPU; `distAt` is the same field evaluated analytically, for
// the sea ring colours and rock placement.
//
//   R: distance to the nearest land hex, 0..DIST_MAX world units (0 on land)
//   G: where the shoreline meets the water, outward from the hex edge (ramp waterline / cliff base), 0..1
//   B: share of that shoreline that is rock (cliff), 0..1
//   A: 255

import * as THREE from 'three';
import { WATER_BIOMES } from '../terrainEdges.js';
import { type ShoreEdge, hexCorner } from './shoreEdges.js';
import { reachAt, waterlineOffset } from './shoreRamp.js';

/** Distances saturate here (the sea is fully "deep" beyond it). */
export const DIST_MAX = 3;
const MARGIN = 3.4;
const MAX_TEXELS_SIDE = 360;
const BUCKET = 2;
/** Land tiles influence the shoreline channels within this distance (smooth hand-over between tiles). */
const REACH_RADIUS = 1.1;

interface LandTile {
  readonly x: number;
  readonly z: number;
  /** Waterline offset of this tile's sea edges (0 = no sea edge). */
  readonly reach: number;
  readonly cliff: number;
}

export interface ShoreField {
  readonly width: number;
  readonly height: number;
  /** World XZ of texel (0, 0)'s corner, and the texel size. */
  readonly ox: number;
  readonly oz: number;
  readonly cell: number;
  readonly data: Uint8Array<ArrayBuffer>;
  /** Exact distance to the nearest land hex at (x, z), capped at DIST_MAX. */
  distAt(x: number, z: number): number;
  /** True when the board has no land (or no water): nothing to shade. */
  readonly empty: boolean;
}

/** Distance from (px, pz) to a pointy-top hex (circumradius 1) centred at (cx, cz); 0 inside. */
export function hexDistance(px: number, pz: number, cx: number, cz: number): number {
  const dx = Math.abs(px - cx);
  const dz = Math.abs(pz - cz);
  const A = Math.cos(Math.PI / 6);
  // Inside test: |dx| <= A and the two slanted edges.
  if (dx <= A && dx * 0.5 + dz * A <= A && dz <= 1) return 0;
  // Fold into the first quadrant and measure to its three candidate segments.
  const pts: ReadonlyArray<readonly [number, number]> = [
    [A, -0.5],
    [A, 0.5],
    [0, 1],
  ];
  let best = Number.POSITIVE_INFINITY;
  for (let k = 0; k < 2; k++) {
    const [x0, z0] = pts[k]!;
    const [x1, z1] = pts[k + 1]!;
    const ex = x1 - x0;
    const ez = z1 - z0;
    const t = Math.max(0, Math.min(1, ((dx - x0) * ex + (dz - z0) * ez) / (ex * ex + ez * ez)));
    best = Math.min(best, Math.hypot(dx - (x0 + ex * t), dz - (z0 + ez * t)));
  }
  return best;
}

/** Per-land-tile shoreline data from the shore edges (average waterline offset, cliff share). */
function landTiles(
  tiles: ReadonlyArray<{ q: number; r: number; x: number; z: number; land: boolean }>,
  edges: ReadonlyArray<ShoreEdge>,
): LandTile[] {
  const byTile = new Map<string, { sum: number; cliff: number; n: number }>();
  for (const e of edges) {
    const k = `${e.q},${e.r}`;
    const slot = byTile.get(k) ?? { sum: 0, cliff: 0, n: 0 };
    let wl: number;
    if (e.kind === 'cliff') {
      // Flared 45 degree wall meets the plane this far out.
      wl = Math.max(0.03, e.top - 0.03 - e.waterY - 0.01);
      slot.cliff++;
    } else {
      const mx = (e.ax + e.bx) / 2;
      const mz = (e.az + e.bz) / 2;
      wl = waterlineOffset(e.top, e.waterY, reachAt(e.waterY, mx, mz));
    }
    slot.sum += Math.min(0.98, wl);
    slot.n++;
    byTile.set(k, slot);
  }
  const out: LandTile[] = [];
  for (const t of tiles) {
    if (!t.land) continue;
    const s = byTile.get(`${t.q},${t.r}`);
    out.push({ x: t.x, z: t.z, reach: s ? s.sum / s.n : 0, cliff: s ? s.cliff / s.n : 0 });
  }
  return out;
}

/**
 * Build the field for a board. `tiles` carries every tile's centre and whether it is land (not ocean / coast
 * / river); `edges` is collectShoreEdges for the same board.
 */
export function buildShoreField(
  tiles: ReadonlyArray<{ q: number; r: number; x: number; z: number; biome: string }>,
  edges: ReadonlyArray<ShoreEdge>,
): ShoreField {
  const flagged = tiles.map((t) => ({ ...t, land: !WATER_BIOMES.has(t.biome) }));
  const land = landTiles(flagged, edges);
  let minX = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let minZ = Number.POSITIVE_INFINITY;
  let maxZ = Number.NEGATIVE_INFINITY;
  for (const t of tiles) {
    minX = Math.min(minX, t.x);
    maxX = Math.max(maxX, t.x);
    minZ = Math.min(minZ, t.z);
    maxZ = Math.max(maxZ, t.z);
  }
  const empty = land.length === 0 || tiles.length === 0;
  if (tiles.length === 0) {
    minX = maxX = minZ = maxZ = 0;
  }
  const ox = minX - MARGIN;
  const oz = minZ - MARGIN;
  const sizeX = maxX - minX + 2 * MARGIN;
  const sizeZ = maxZ - minZ + 2 * MARGIN;
  const cell = Math.max(0.12, Math.max(sizeX, sizeZ) / MAX_TEXELS_SIDE);
  const width = Math.max(2, Math.ceil(sizeX / cell));
  const height = Math.max(2, Math.ceil(sizeZ / cell));

  const buckets = new Map<number, LandTile[]>();
  const bucketCoords: Array<[number, number]> = [];
  const bk = (bx: number, bz: number) => bx * 100003 + bz;
  for (const t of land) {
    const bx = Math.floor(t.x / BUCKET);
    const bz = Math.floor(t.z / BUCKET);
    const key = bk(bx, bz);
    const list = buckets.get(key);
    if (list) list.push(t);
    else {
      buckets.set(key, [t]);
      bucketCoords.push([bx, bz]);
    }
  }
  const span = Math.ceil((DIST_MAX + 1.1) / BUCKET);
  // Buckets within `span` of any land: a texel elsewhere is open sea at DIST_MAX without scanning.
  const active = new Set<number>();
  for (const [bx, bz] of bucketCoords) {
    for (let i = -span; i <= span; i++)
      for (let j = -span; j <= span; j++) active.add(bk(bx + i, bz + j));
  }

  const query = (x: number, z: number, wantReach: boolean) => {
    const bx = Math.floor(x / BUCKET);
    const bz = Math.floor(z / BUCKET);
    if (!active.has(bk(bx, bz))) return { dist: DIST_MAX, reach: 0, cliff: 0 };
    let dist = DIST_MAX;
    let wSum = 0;
    let rSum = 0;
    let cSum = 0;
    for (let i = -span; i <= span; i++) {
      for (let j = -span; j <= span; j++) {
        const list = buckets.get(bk(bx + i, bz + j));
        if (!list) continue;
        for (const t of list) {
          // Cheap reject before the exact distance.
          const rough = Math.hypot(x - t.x, z - t.z) - 1;
          if (rough > Math.max(DIST_MAX, REACH_RADIUS)) continue;
          const d = hexDistance(x, z, t.x, t.z);
          if (d < dist) dist = d;
          if (wantReach && t.reach > 0 && d < REACH_RADIUS) {
            const w = (1 - d / REACH_RADIUS) ** 2;
            wSum += w;
            rSum += w * t.reach;
            cSum += w * t.cliff;
          }
        }
      }
    }
    return { dist, reach: wSum > 0 ? rSum / wSum : 0, cliff: wSum > 0 ? cSum / wSum : 0 };
  };

  const data = new Uint8Array(width * height * 4);
  data.fill(255);
  if (!empty) {
    for (let j = 0; j < height; j++) {
      for (let i = 0; i < width; i++) {
        const x = ox + (i + 0.5) * cell;
        const z = oz + (j + 0.5) * cell;
        const q = query(x, z, true);
        const o = (j * width + i) * 4;
        data[o] = Math.round((q.dist / DIST_MAX) * 255);
        data[o + 1] = Math.round(Math.min(1, q.reach) * 255);
        data[o + 2] = Math.round(Math.min(1, q.cliff) * 255);
      }
    }
  }
  return {
    width,
    height,
    ox,
    oz,
    cell,
    data,
    empty,
    distAt: (x, z) => (empty ? DIST_MAX : query(x, z, false).dist),
  };
}

/** Corner positions of a hex (re-exported for tests that walk the shoreline). */
export { hexCorner };

/** GPU texture for a field (RGBA8, bilinear, clamped). The caller owns and disposes it. */
export function shoreFieldTexture(field: ShoreField): THREE.DataTexture {
  const tex = new THREE.DataTexture(field.data, field.width, field.height, THREE.RGBAFormat);
  tex.magFilter = THREE.LinearFilter;
  tex.minFilter = THREE.LinearFilter;
  tex.wrapS = THREE.ClampToEdgeWrapping;
  tex.wrapT = THREE.ClampToEdgeWrapping;
  tex.generateMipmaps = false;
  tex.needsUpdate = true;
  return tex;
}
